/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BUTTON_FORMAT_TRANSPARENT, BUTTON_THEME_DEFAULT, Button, TextField, Tooltip } from '@squiz/sds';
import isNaN from 'lodash/isNaN';
import isUndefined from 'lodash/isUndefined';
import parseInt from 'lodash/parseInt';
import property from 'lodash/property';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { SEGMENT_BUILDER_DATA_IDS, SEGMENT_BUILDER_FORM_SELECT_IDS } from '../../SegmentBuilder.constants';
import { SelectSegmentAttribute } from '../../elements/SelectSegmentAttribute/SelectSegmentAttribute';
import { SelectSegmentRule } from '../../elements/SelectSegmentRule/SelectSegmentRule';
import { SELECT_TYPE_NUMERIC, SELECT_TYPE_STRING } from '../../elements/SelectSegmentRule/SelectSegmentRule.props';
import { SegmentRuleProps } from './SegmentRule.props';

const ICON_REMOVE_CIRCLE = (
  <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 12c0 .55.45 1 1 1h8c.55 0 1-.45 1-1s-.45-1-1-1H8c-.55 0-1 .45-1 1zm5-10C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"></path>
  </svg>
);

/**
 * The rule component of the segment builder.
 */
export const SegmentRule = (props: SegmentRuleProps): ReactElement => {
  const { t } = useTranslation();
  const {
    deleteFunction,
    errors = {},
    events,
    isLastRule,
    onChangeAttribute,
    onChangeInput,
    onChangeRule,
    ruleData,
    ruleset,
    schema,
    type,
  } = props;
  const { comparison, id, property: prop, value } = ruleData;
  const [expiry, setExpiry] = useState<string>(``);

  // Type of relevance is ignored here and assumes the value is 'days'.
  // At this time days is the only config available.
  const setExpiryFriendlyValue = useCallback(
    (value: number | undefined): void => {
      if (isUndefined(value) || isNaN(value)) {
        setExpiry(``);
      } else if (value === 1) {
        setExpiry(`${value} day`);
      } else {
        setExpiry(`${value} days`);
      }
    },
    [setExpiry],
  );

  useEffect((): void => {
    const value = parseInt(property(`${ruleData?.property?.replace(`$event.`, ``)}.relevance.value`)(events) as string);
    setExpiryFriendlyValue(value);
  }, [events, ruleData, setExpiryFriendlyValue]);

  // Labels
  const labels = useMemo(() => {
    return {
      countFieldLabel: t(CDP_STRINGS.countFieldLabel),
      countFieldPlaceholder: t(CDP_STRINGS.countFieldPlaceholder),
      deleteRuleLabel: t(CDP_STRINGS.ruleButtonTextDelete),
      timePeriodLabel: t(CDP_STRINGS.timePeriodLabel),
      valueFieldLabel: t(CDP_STRINGS.valueFieldLabel),
      valueFieldPlaceholder: t(CDP_STRINGS.valueFieldPlaceholder),
    };
  }, [t]);

  if (type === `event-property`) {
    return (
      <div className="rules-section rules-section__event" data-id={SEGMENT_BUILDER_DATA_IDS.segmentRule}>
        <div>
          <SelectSegmentAttribute
            errors={errors}
            events={events}
            onChange={(ref, v): void => {
              const expiryValue = parseInt(property(`${v.replace(`$event.`, ``)}.relevance.value`)(events) as string);
              setExpiryFriendlyValue(expiryValue);
              if (onChangeAttribute) {
                onChangeAttribute(ref, v);
              }
            }}
            preselectedAttribute={prop}
            ruleId={id}
            schema={schema}
            type={type || `default`}
          />
        </div>
        <div>
          <SelectSegmentRule
            dataType={SELECT_TYPE_NUMERIC}
            errors={errors}
            onChange={(ref, v): void => {
              if (onChangeRule) {
                onChangeRule(ref, v);
              }
            }}
            preselectedRule={comparison}
            ruleId={id}
          />
        </div>
        <div>
          <TextField
            data-id={SEGMENT_BUILDER_DATA_IDS.countValueInput}
            data-rule={id}
            data-ruleset={ruleset}
            errorMessage={
              property(`${SEGMENT_BUILDER_FORM_SELECT_IDS.count}-${id}`)(errors) as string | null | undefined
            }
            fieldLabel={labels.countFieldLabel}
            id={`${SEGMENT_BUILDER_FORM_SELECT_IDS.count}-${id}`}
            name={SEGMENT_BUILDER_FORM_SELECT_IDS.count}
            onChange={onChangeInput}
            placeholder={labels.countFieldPlaceholder}
            required
            value={value}
          />
        </div>
        <div>
          <span className="sds-form-label rules-section__text">in the last</span>
        </div>
        <div>
          <TextField
            classes={`segment-rule__expiry`}
            data-id={SEGMENT_BUILDER_DATA_IDS.daysValueInput}
            data-rule={id}
            data-ruleset={ruleset}
            disabled
            fieldLabel={labels.timePeriodLabel}
            id={`${SEGMENT_BUILDER_FORM_SELECT_IDS.days}-${id}`}
            name={SEGMENT_BUILDER_FORM_SELECT_IDS.days}
            value={expiry}
          />
        </div>
        <div className="segment-rule__delete-button-container">
          {!isLastRule ? (
            <>
              <div className="segment-rule__delete-button-container-spacer"></div>
              <Tooltip label={labels.deleteRuleLabel}>
                <Button
                  aria-label={labels.deleteRuleLabel}
                  data-id={SEGMENT_BUILDER_DATA_IDS.buttonDeleteRule}
                  data-rule={id}
                  data-ruleset={ruleset}
                  format={BUTTON_FORMAT_TRANSPARENT}
                  icon={ICON_REMOVE_CIRCLE}
                  onClick={deleteFunction}
                  theme={BUTTON_THEME_DEFAULT}
                />
              </Tooltip>
            </>
          ) : (
            <>
              <div className="segment-rule__delete-button-container-spacer"></div>
              <div>
                <Button
                  aria-label={labels.deleteRuleLabel}
                  data-id={SEGMENT_BUILDER_DATA_IDS.buttonDeleteRule}
                  data-rule={id}
                  data-ruleset={ruleset}
                  disabled
                  format={BUTTON_FORMAT_TRANSPARENT}
                  icon={ICON_REMOVE_CIRCLE}
                  theme={BUTTON_THEME_DEFAULT}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  } else {
    return (
      <div className="rules-section rules-section__data" data-id={SEGMENT_BUILDER_DATA_IDS.segmentRule}>
        <div>
          <SelectSegmentAttribute
            errors={errors}
            events={events}
            onChange={(ref, v): void => {
              if (onChangeAttribute) {
                onChangeAttribute(ref, v);
              }
            }}
            preselectedAttribute={prop}
            ruleId={id}
            schema={schema}
            type={type || `default`}
          />
        </div>
        <div>
          <SelectSegmentRule
            dataType={SELECT_TYPE_STRING}
            errors={errors}
            onChange={(ref, v): void => {
              if (onChangeRule) {
                onChangeRule(ref, v);
              }
            }}
            preselectedRule={comparison}
            ruleId={id}
          />
        </div>
        <div>
          <TextField
            data-id={SEGMENT_BUILDER_DATA_IDS.segmentValueInput}
            data-rule={id}
            data-ruleset={ruleset}
            errorMessage={
              property(`${SEGMENT_BUILDER_FORM_SELECT_IDS.value}-${id}`)(errors) as string | null | undefined
            }
            fieldLabel={labels.valueFieldLabel}
            id={`${SEGMENT_BUILDER_FORM_SELECT_IDS.value}-${id}`}
            name={SEGMENT_BUILDER_FORM_SELECT_IDS.value}
            onChange={onChangeInput}
            placeholder={labels.valueFieldPlaceholder}
            required
            value={value}
          />
        </div>
        <div className="segment-rule__delete-button-container">
          {!isLastRule ? (
            <>
              <div className="segment-rule__delete-button-container-spacer"></div>
              <Tooltip label={labels.deleteRuleLabel}>
                <Button
                  aria-label={labels.deleteRuleLabel}
                  data-id={SEGMENT_BUILDER_DATA_IDS.buttonDeleteRule}
                  data-rule={id}
                  data-ruleset={ruleset}
                  format={BUTTON_FORMAT_TRANSPARENT}
                  icon={ICON_REMOVE_CIRCLE}
                  onClick={deleteFunction}
                  theme={BUTTON_THEME_DEFAULT}
                />
              </Tooltip>
            </>
          ) : (
            <>
              <div className="segment-rule__delete-button-container-spacer"></div>
              <Button
                aria-label={labels.deleteRuleLabel}
                data-id={SEGMENT_BUILDER_DATA_IDS.buttonDeleteRule}
                data-rule={id}
                data-ruleset={ruleset}
                disabled
                format={BUTTON_FORMAT_TRANSPARENT}
                icon={ICON_REMOVE_CIRCLE}
                theme={BUTTON_THEME_DEFAULT}
              />
            </>
          )}
        </div>
      </div>
    );
  }
};
